import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from '../../lib/trpc';
import { idem } from '../../lib/idempotency';
import { IconCheck } from '../icons';

import type { DerivedGroup, DerivedItem } from './unsentTicks';

type Item = DerivedItem;
type Group = DerivedGroup;

/**
 * The form at the door (PRD v2 §2). A tech works it one thumb at a time, so the whole row
 * is the target — not a 20px checkbox — and a required item that's still open says so on
 * the row rather than only at the moment the finish button refuses.
 *
 * Read-only once the stop is finished: at that point the list stops being a task and
 * becomes the record of what was inspected.
 */
export function VisitChecklist({
  checklists,
  frozen,
  visitId,
}: {
  /**
   * Already folded with the writes still sitting on this phone — see `withUnsentTicks`.
   * The type is `DerivedGroup`, not the wire shape, so a caller that hands over the raw
   * server answer is a compile error rather than a screen that quietly under-counts.
   */
  checklists: Group[];
  frozen: boolean;
  visitId: string;
}) {
  if (checklists.length === 0) return null;

  return (
    <>
      {checklists.map((group) => (
        <ChecklistGroup
          key={group.jobChecklistId}
          group={group}
          frozen={frozen}
          visitId={visitId}
        />
      ))}
    </>
  );
}

function ChecklistGroup({
  group,
  frozen,
  visitId,
}: {
  group: Group;
  frozen: boolean;
  visitId: string;
}) {
  const done = group.items.filter((i) => i.completedAt).length;
  const complete = done === group.items.length;

  return (
    <section className="rounded-[var(--radius)] border border-border bg-surface p-3">
      <div className="mb-2 flex items-baseline justify-between gap-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {group.name}
        </p>
        <span
          className={`text-xs tabular-nums ${
            complete ? 'text-[var(--sw-success-text)]' : 'text-muted-foreground'
          }`}
        >
          {done} of {group.items.length}
        </span>
      </div>

      {group.requiredOpen > 0 && !frozen ? (
        <p className="mb-2 text-xs text-[var(--sw-warn-text)]">
          {group.requiredOpen === 1
            ? '1 step must be done before you can finish.'
            : `${group.requiredOpen} steps must be done before you can finish.`}
        </p>
      ) : null}

      <ul className="space-y-1.5">
        {group.items.map((item) => (
          <ChecklistRow key={item.id} item={item} frozen={frozen} visitId={visitId} />
        ))}
      </ul>
    </section>
  );
}

function ChecklistRow({
  item,
  frozen,
  visitId,
}: {
  item: Item;
  frozen: boolean;
  visitId: string;
}) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const [note, setNote] = useState(item.note ?? '');
  const [noteOpen, setNoteOpen] = useState(Boolean(item.note));

  // The row is optimistic: a tech on a bad connection must see the tick land instantly,
  // and the server's answer replaces it a moment later.
  const [pending, setPending] = useState<boolean | null>(null);
  useEffect(() => setPending(null), [item.completedAt]);

  const set = useMutation(
    trpc.checklist.setItem.mutationOptions({
      onSuccess: () => void qc.invalidateQueries(),
      onError: () => setPending(null),
    }),
  );

  /**
   * Three sources, most local first.
   *
   * `pending` covers the moment between the tap and the mutation existing, and the window
   * after a success where the refetch has not landed yet. `unsentDone` covers what `pending`
   * structurally cannot: it is component state, so closing the app throws it away — and the
   * tech reopening in the same crawlspace would have found the row UNTICKED while the count
   * said the step was done. Two readings of one fact, disagreeing, which is the defect this
   * whole ticket is about, one level down.
   */
  const checked = pending ?? item.unsentDone ?? Boolean(item.completedAt);

  const toggle = () => {
    if (frozen) return;
    const next = !checked;
    setPending(next);
    set.mutate({ itemId: item.id, visitId, done: next, ...idem() });
  };

  const saveNote = () => {
    const trimmed = note.trim();
    if (trimmed === (item.note ?? '')) return;
    // No `done` — a reading is not a tick, and sending one would toggle the box.
    set.mutate({ itemId: item.id, visitId, note: trimmed, ...idem() });
  };

  return (
    <li>
      <button
        type="button"
        onClick={toggle}
        disabled={frozen}
        aria-pressed={checked}
        className={`flex min-h-[44px] w-full items-center gap-3 rounded-[var(--radius)] border px-2 py-1.5 text-left ${
          checked ? 'border-transparent bg-surface-2' : 'border-border'
        } ${frozen ? 'cursor-default' : ''}`}
      >
        <span
          aria-hidden
          className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-[var(--radius-sm)] border ${
            checked
              ? 'border-transparent bg-[var(--sw-success-text)] text-[var(--sw-surface)]'
              : item.required
                ? 'border-[var(--sw-warn-text)]'
                : 'border-border'
          }`}
        >
          {checked ? <IconCheck size={15} /> : null}
        </span>
        <span
          className={`min-w-0 flex-1 text-sm ${checked ? 'text-muted-foreground' : ''}`}
        >
          {item.label}
          {item.required && !checked ? (
            <span className="ml-1.5 text-xs font-semibold text-[var(--sw-warn-text)]">
              Required
            </span>
          ) : null}
        </span>
      </button>

      {noteOpen ? (
        <textarea
          className="mt-1 min-h-[44px] w-full rounded-[var(--radius)] border border-border bg-surface p-2 text-sm"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          onBlur={saveNote}
          placeholder="Reading, model number, what you saw…"
          aria-label={`Note for ${item.label}`}
          disabled={frozen}
        />
      ) : frozen ? null : (
        <button
          type="button"
          onClick={() => setNoteOpen(true)}
          className="ml-11 mt-0.5 text-xs text-muted-foreground underline-offset-2 hover:underline"
        >
          Add a reading
        </button>
      )}
    </li>
  );
}
